import styled from "styled-components";
import { Overlay } from "../../../styles/popup";
import { useState } from "react";

const Popup = styled.div`
  width: min(480px, calc(100vw - 48px));
  border-radius: 12px;
  background: #fff;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;

  > .title {
    color: #121212;
    font-family: Pretendard;
    font-size: 18px;
    font-weight: 700;
  }

  > img {
    width: 17px;
    height: 17px;
    cursor: pointer;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: #e8e8e8;
`;

const Body = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  color: #666;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.6;
  word-break: keep-all;

  > p {
    margin: 0;
    > .confirm-phrase {
      color: #d91818;
      font-weight: 700;
    }
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  > label {
    color: #121212;
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 600;
  }

  > form {
    width: 100%;
  }

  > input,
  > form > input {
    width: 100%;
    height: 42px;
    padding: 0 12px;
    border: 1px solid #d9d9d9;
    border-radius: 8px;
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 500;
    color: #121212;
    outline: none;
    box-sizing: border-box;

    &::placeholder {
      color: #b7b7b7;
    }

    &:focus {
      border-color: #7f25fb;
    }
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px 24px 20px;

  > button {
    min-width: 72px;
    height: 40px;
    padding: 0 16px;
    border: none;
    border-radius: 8px;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
  }

  > .cancel {
    background: #e8e8e8;
    color: #121212;
  }

  > .confirm {
    background: #d91818;
    color: #fff;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`;

interface ProgressResetConfirmPopupProps {
  title: string;
  description: string;
  confirmPhrase: string;
  submitting?: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
}

const ProgressResetConfirmPopup = ({
  title,
  description,
  confirmPhrase,
  submitting = false,
  onClose,
  onConfirm,
}: ProgressResetConfirmPopupProps) => {
  const [input, setInput] = useState("");
  const matched = input.trim() === confirmPhrase;

  const handleConfirm = async () => {
    if (!matched || submitting) return;
    await onConfirm();
  };

  return (
    <Overlay $zIndex={1100}>
      <Popup>
        <Header>
          <div className="title">{title}</div>
          <img
            src="/image-web/tournament/close.svg"
            alt="close"
            onClick={submitting ? undefined : onClose}
          />
        </Header>

        <Divider />

        <Body>
          <p>{description}</p>
          <p>
            계속하려면 아래에{" "}
            <span className="confirm-phrase">{confirmPhrase}</span> 를
            입력하세요.
          </p>
          <Field>
            <label htmlFor="progress-reset-confirm">확인 문구</label>
            <form
              autoComplete="off"
              onSubmit={(e) => {
                e.preventDefault();
                handleConfirm();
              }}
            >
              {/* Chrome은 autocomplete 속성을 무시하는 경우가 많아, 포커스 전 readOnly로 자동완성을 막음 */}
              <input
                id="progress-reset-confirm"
                name={`confirm-phrase-${Date.now()}`}
                type="text"
                value={input}
                disabled={submitting}
                placeholder={confirmPhrase}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                readOnly
                data-1p-ignore
                data-lpignore="true"
                data-form-type="other"
                onFocus={(e) => e.currentTarget.removeAttribute("readOnly")}
                onChange={(e) => setInput(e.target.value)}
              />
            </form>
          </Field>
        </Body>

        <Divider />

        <Footer>
          <button
            type="button"
            className="cancel"
            disabled={submitting}
            onClick={onClose}
          >
            취소
          </button>
          <button
            type="button"
            className="confirm"
            disabled={!matched || submitting}
            onClick={handleConfirm}
          >
            {submitting ? "초기화 중..." : "초기화"}
          </button>
        </Footer>
      </Popup>
    </Overlay>
  );
};

export default ProgressResetConfirmPopup;
